import {Dropdown, IconButton, Tooltip, useTranslationContext} from 'platform/components';

import {type RefObject, useCallback, useState} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useEagGridStore} from '../../../../store/useEagGridStore';
import type {EagColumnState} from '../../../../types/EagColumnState';
import {scrollToColumn} from '../../../../utils/scrollToColumn';
import {FindColumnDropdown, type FindColumnEntry} from './FindColumnDropdown';

type FindColumnButtonProps = {
  columns: FindColumnEntry[];
  columnStates: EagColumnState[];
  tableRef: RefObject<HTMLTableElement | null>;
} & TestIdProps;

export function FindColumnButton(props: FindColumnButtonProps) {
  const t = useTranslationContext();
  const store = useEagGridStore();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = useCallback(
    (columnKey: string) => {
      // Unhide the column if it's currently hidden
      const colState = props.columnStates.find((cs) => cs.key === columnKey);
      const isHidden = colState?.hidden === true;

      if (isHidden) {
        store.setColumnHidden(columnKey, false);
      }

      setIsOpen(false);

      // Wait for DOM to update. When a column was just un-hidden, the store
      // update triggers a React re-render + AG-Grid DOM mutation. A single rAF
      // fires before the new <th> is painted, so we need a second rAF to
      // ensure the column element is queryable by scrollToColumn.
      const scroll = () => {
        if (props.tableRef.current) {
          scrollToColumn(props.tableRef.current, columnKey);
        }
      };

      if (isHidden) {
        requestAnimationFrame(() => requestAnimationFrame(scroll));
      } else {
        requestAnimationFrame(scroll);
      }
    },
    [props.columnStates, props.tableRef, store]
  );

  return (
    <Dropdown
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement="bottom-end"
      hasSearch
      isHeightLimited
      dropdownControl={
        <Tooltip label={t('page.datagrid.labels.findColumn')}>
          <IconButton
            aria-label={t('page.datagrid.labels.findColumn')}
            icon="action/find_in_page"
            priority={isOpen ? 'secondary' : undefined}
            data-testid={suffixTestId('findColumnButton', props)}
          />
        </Tooltip>
      }
    >
      <FindColumnDropdown
        columns={props.columns}
        columnStates={props.columnStates}
        onSelect={handleSelect}
        data-testid={props['data-testid']}
      />
    </Dropdown>
  );
}
